/**
 * Sensor math for ride telemetry — pure functions, no DOM. The useMotion hook
 * owns the devicemotion/deviceorientation plumbing; everything in this file
 * transfers as-is to expo-sensors when the native app lands (Phase 6).
 *
 * Coordinate conventions (W3C device frame): x → right edge of the screen,
 * y → top edge, z → out of the screen. `accelerationIncludingGravity` at rest
 * face-up reads {0, 0, +9.81} per spec; iOS WebKit inverts all accelerometer
 * signs — that's normalised in the hook before anything reaches this file.
 */

export type Vec3 = readonly [number, number, number];
export type Mat3 = readonly number[]; // 9 entries, row-major

export const STANDARD_GRAVITY = 9.80665; // m/s²

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function magnitude(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

export function scale(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

export function subtract(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function normalize(v: Vec3): Vec3 | null {
  const m = magnitude(v);
  return m < 1e-6 ? null : scale(v, 1 / m);
}

/**
 * The bike's axes expressed in phone coordinates, captured at calibration
 * (bike upright and stationary). All three are unit vectors.
 */
export interface BikeFrame {
  up: Vec3;
  forward: Vec3;
  right: Vec3;
}

/**
 * Derives the bike frame from the mean gravity reaction measured while
 * stationary: up is the measured reaction direction; forward is the back of
 * the phone (−z) projected onto the plane ⊥ up — correct for any mount where
 * the screen faces the rider, portrait or landscape. A phone lying flat (tank
 * bag) falls back to the top edge of the screen (+y) as forward instead.
 */
export function deriveBikeFrame(meanGravity: Vec3): BikeFrame | null {
  const up = normalize(meanGravity);
  if (!up) return null;
  const candidates: Vec3[] = [
    [0, 0, -1],
    [0, 1, 0],
  ];
  for (const candidate of candidates) {
    const projected = subtract(candidate, scale(up, dot(candidate, up)));
    // Reject near-parallel candidates — the projection direction is then mostly noise.
    if (magnitude(projected) < 0.3) continue;
    const forward = normalize(projected);
    if (forward) return { up, forward, right: cross(forward, up) };
  }
  return null;
}

/**
 * Mean and worst per-sample deviation (m/s²) over a calibration window.
 * The deviation is the gate for "were you actually holding still?".
 */
export function calibrationStats(samples: Vec3[]): { mean: Vec3; maxDeviation: number } {
  let sx = 0;
  let sy = 0;
  let sz = 0;
  for (const [x, y, z] of samples) {
    sx += x;
    sy += y;
    sz += z;
  }
  const n = samples.length;
  const mean: Vec3 = [sx / n, sy / n, sz / n];
  let maxDeviation = 0;
  for (const s of samples) {
    maxDeviation = Math.max(maxDeviation, magnitude(subtract(s, mean)));
  }
  return { mean, maxDeviation };
}

/**
 * Rotation matrix (device frame → earth frame) from the W3C deviceorientation
 * Euler angles: intrinsic Z(alpha)·X(beta)·Y(gamma). For non-absolute events
 * the yaw reference is arbitrary, but the vertical is correct — which is all
 * the lean computation needs.
 */
export function orientationMatrix(alphaDeg: number, betaDeg: number, gammaDeg: number): Mat3 {
  const a = (alphaDeg * Math.PI) / 180;
  const b = (betaDeg * Math.PI) / 180;
  const g = (gammaDeg * Math.PI) / 180;
  const cA = Math.cos(a);
  const sA = Math.sin(a);
  const cB = Math.cos(b);
  const sB = Math.sin(b);
  const cG = Math.cos(g);
  const sG = Math.sin(g);
  return [
    cA * cG - sA * sB * sG, -sA * cB, cA * sG + sA * sB * cG,
    sA * cG + cA * sB * sG, cA * cB, sA * sG - cA * sB * cG,
    -cB * sG, sB, cB * cG,
  ];
}

export function applyMatrix(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

/**
 * Signed lean angle in degrees from the fused orientation: rotation of the
 * bike's up axis away from earth vertical, about the bike's forward axis.
 * Positive = leaning right. No calibration reference needed — the frame's up
 * came from gravity, so this reads ~0 at the calibration pose by construction.
 *
 * Known limit: the platform's fusion uses the accelerometer as its long-term
 * gravity reference, and in a steady corner the net specific force aligns
 * with the *bike*, so this drifts toward under-reading the longer a corner is
 * held. The kinematic estimate doesn't have that failure mode — compare both.
 */
export function leanFromOrientation(m: Mat3, frame: BikeFrame): number {
  const upEarth = applyMatrix(m, frame.up);
  const forwardEarth = applyMatrix(m, frame.forward);
  const vertical: Vec3 = [0, 0, 1];
  const sinPart = dot(cross(vertical, upEarth), forwardEarth);
  const cosPart = dot(upEarth, vertical);
  return (Math.atan2(sinPart, cosPart) * 180) / Math.PI;
}

/**
 * Kinematic lean estimate for a coordinated (balanced) turn:
 * tan(lean) = v·ω / g. `yawRateRadS` is positive when turning right
 * (clockwise seen from above), so positive result = leaning right, matching
 * leanFromOrientation. Slightly under-reads at deep lean because the gyro
 * measures yaw about the bike's tilted up axis, not earth vertical.
 */
export function leanKinematic(speedMps: number, yawRateRadS: number): number {
  return (Math.atan2(speedMps * yawRateRadS, STANDARD_GRAVITY) * 180) / Math.PI;
}

/**
 * One-pole low-pass step. Engine vibration on a bike swamps the braking /
 * cornering signal raw; tau around 150–250 ms keeps the readout glanceable
 * without feeling laggy.
 */
export function lowpass(prev: number, next: number, dtMs: number, tauMs: number): number {
  const blend = dtMs / (tauMs + dtMs);
  return prev + blend * (next - prev);
}
